import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { constants } from '../lib/constants';
import { readAccessToken, clearSession, loginPathFor } from '../lib/session';

/**
 * Puerta de las pantallas privadas (Customer Portal y panel de Afiliados).
 *
 * Dos capas, porque una sola no basta:
 *  1. Sin token no se renderiza NADA: se redirige al login antes de pintar.
 *     (Antes bastaba con escribir la URL para ver el panel entero.)
 *  2. Que exista una cadena en localStorage no es estar autenticado: el token
 *     puede estar caducado o inventado. Se valida contra un endpoint
 *     autenticado y, si lo rechaza (401/403), se cierra la sesión.
 *
 * Un fallo de RED no expulsa a nadie: no se echa al usuario por estar sin
 * cobertura. La autorización de verdad la sigue haciendo el backend en cada
 * endpoint; esto es la capa de interfaz.
 *
 * Enlaces directos (solo Customer Portal): al mandar al login se lleva la URL
 * pedida en `?next=` (p. ej. `/customer-portal/dashboard?panel=embeds`) para
 * volver a ella tras iniciar sesión. Quien decide si ese destino es seguro es
 * el login (`CustomerPortal.jsx`), que solo acepta rutas del propio dashboard.
 * El panel de Afiliados no cambia: vuelve a su login sin parámetros.
 */
const RequireSession = ({ kind = 'portal', children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = readAccessToken(kind);
  const loginPath = loginPathFor(kind);
  const loginTarget =
    kind === 'portal'
      ? `${loginPath}?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`
      : loginPath;

  // El destino va en una ref: si cambiara la query de la página no hace falta
  // volver a validar el token contra el servidor.
  const loginTargetRef = useRef(loginTarget);
  loginTargetRef.current = loginTarget;

  useEffect(() => {
    if (!accessToken) return undefined;
    let alive = true;
    fetch(constants.readUsersCompany, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!alive) return;
        if (res.status === 401 || res.status === 403) {
          clearSession(kind);
          navigate(loginTargetRef.current, { replace: true });
        }
      })
      .catch(() => {
        /* Sin red no se echa al usuario. */
      });
    return () => {
      alive = false;
    };
  }, [accessToken, kind, navigate]);

  if (!accessToken) {
    return <Navigate to={loginTarget} replace />;
  }

  return children;
};

export default RequireSession;
